const TRIGGER_SELECTOR = '.js-inquiry-modal-trigger';
const FORM_SELECTOR = '.js-inquiry-form';

/**
 * Wires up interest-card selection, client-side validation, and an AJAX
 * submit (against Shopify's native contact form, so it reaches the store's
 * notification email without a full page navigation) for one instance of
 * the inquiry form. Shared by both the site-wide modal and the inline form
 * on the Work With Us page — same fields (snippets/inquiry-form-fields.liquid),
 * same behavior, the only difference is whether `root` sits inside a
 * <dialog-component> (modal) or not (always-visible page form).
 *
 * @param {Element} root - a ".js-inquiry-form" element
 * @returns {null | {
 *   interestButtons: NodeListOf<Element>,
 *   interestField: Element | null,
 *   sourceField: Element | null,
 *   triggerField: Element | null,
 *   productField: Element | null,
 *   dialogHost: Element | null,
 *   syncInterestField: () => void,
 * }}
 */
function setupInquiryForm(root) {
  const form = root.querySelector('.inquiry-form');
  const interestButtons = root.querySelectorAll('[data-interest-card]');
  const interestField = root.querySelector('[data-inquiry-field="interest"]');
  const interestsError = root.querySelector('[data-inquiry-interests-error]');
  const sourceField = root.querySelector('[data-inquiry-field="source"]');
  const triggerField = root.querySelector('[data-inquiry-field="trigger"]');
  const productField = root.querySelector('[data-inquiry-field="product"]');
  const submitError = root.querySelector('[data-inquiry-submit-error]');
  const formView = root.querySelector('[data-inquiry-view="form"]');
  const successView = root.querySelector('[data-inquiry-view="success"]');
  const dialogHost = root.closest('dialog-component');

  if (!(form instanceof HTMLFormElement)) return null;

  // Fallback path for the rare case where a visitor submits before the AJAX
  // handler below has attached (e.g. clicking Submit before this module
  // script finishes loading) — the browser falls through to a real POST,
  // and Shopify's contact form redirects to `return_to` (see
  // snippets/inquiry-form-fields.liquid), which we've set to include
  // `contact_posted=true`. On the resulting fresh page load, jump straight
  // to the success view instead of showing an empty form again, then strip
  // the marker from the URL so a refresh doesn't re-trigger it.
  if (new URLSearchParams(window.location.search).get('contact_posted') === 'true') {
    if (formView) formView.hidden = true;
    if (successView) successView.hidden = false;

    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('contact_posted');
    window.history.replaceState(null, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
  }

  // A modal instance gets its source/trigger fields from whichever element
  // opened it (see initInquiryForms below). An inline page form is always
  // visible — there's no "opening" click to capture that from — so fill
  // those in immediately instead of leaving them blank.
  if (!dialogHost) {
    if (sourceField instanceof HTMLInputElement) sourceField.value = window.location.href;
    if (triggerField instanceof HTMLInputElement) triggerField.value = document.title;
  }

  // --- Interest cards: multi-select, role="checkbox" ---
  function syncInterestField() {
    if (!(interestField instanceof HTMLInputElement)) return;

    const selected = Array.from(interestButtons)
      .filter((button) => button.getAttribute('aria-checked') === 'true')
      .map((button) => button.getAttribute('data-value') || '');

    interestField.value = selected.join(', ');

    if (selected.length > 0 && interestsError) interestsError.hidden = true;
  }

  interestButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const isChecked = button.getAttribute('aria-checked') === 'true';
      button.setAttribute('aria-checked', isChecked ? 'false' : 'true');
      syncInterestField();
    });
  });

  // --- Validation ---
  /** @param {HTMLElement} field */
  function fieldWrapper(field) {
    return field.closest('.inquiry-form__field');
  }

  /** @returns {boolean} whether the form is valid */
  function validate() {
    let isValid = true;
    const requiredFields = form.querySelectorAll('[required]');

    requiredFields.forEach((field) => {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
        return;
      }

      const wrapper = fieldWrapper(field);
      const valid = field.checkValidity() && field.value.trim() !== '';

      if (wrapper) wrapper.classList.toggle('is-invalid', !valid);
      if (!valid) isValid = false;
    });

    const hasInterestSelected = Array.from(interestButtons).some(
      (button) => button.getAttribute('aria-checked') === 'true'
    );

    if (interestsError) interestsError.hidden = hasInterestSelected;
    if (!hasInterestSelected) isValid = false;

    return isValid;
  }

  // Clear error state as the visitor fixes a field
  form.querySelectorAll('[required]').forEach((field) => {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
      return;
    }

    const eventName = field instanceof HTMLSelectElement ? 'change' : 'input';

    field.addEventListener(eventName, () => {
      const wrapper = fieldWrapper(field);
      const valid = field.checkValidity() && field.value.trim() !== '';
      if (wrapper && valid) wrapper.classList.remove('is-invalid');
    });
  });

  // --- Submit via fetch, no page navigation ---
  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (submitError) submitError.hidden = true;

    if (!validate()) {
      const firstInvalid = form.querySelector('.is-invalid input, .is-invalid select, .is-invalid textarea');
      if (firstInvalid instanceof HTMLElement) firstInvalid.focus();
      return;
    }

    const submitButton = form.querySelector('.inquiry-form__submit');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      if (formView) formView.hidden = true;
      if (successView) successView.hidden = false;
      form.reset();
      interestButtons.forEach((button) => button.setAttribute('aria-checked', 'false'));
      syncInterestField();
    } catch (error) {
      if (submitError) submitError.hidden = false;
    } finally {
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
    }
  });

  // --- Reset to the form view after the close animation finishes — modal only,
  // an inline page form has no "close" to reset on, so it just stays on the
  // success view once submitted.
  if (dialogHost) {
    dialogHost.addEventListener('dialog:close', () => {
      window.setTimeout(() => {
        if (formView) formView.hidden = false;
        if (successView) successView.hidden = true;
        form.querySelectorAll('.is-invalid').forEach((el) => el.classList.remove('is-invalid'));
        if (interestsError) interestsError.hidden = true;
      }, 300);
    });
  }

  return { interestButtons, interestField, sourceField, triggerField, productField, dialogHost, syncInterestField };
}

/**
 * Initializes every inquiry-form instance on the page, then — only for
 * whichever one is the site-wide modal — wires up the "open on click of any
 * .js-inquiry-modal-trigger, anywhere on the page" behavior, since that's
 * meaningless for an always-visible inline form.
 */
function initInquiryForms() {
  const instances = new Map();

  document.querySelectorAll(FORM_SELECTOR).forEach((root) => {
    const handles = setupInquiryForm(root);
    if (handles) instances.set(root, handles);
  });

  const modalRoot = document.querySelector(`${FORM_SELECTOR}.inquiry-modal`);
  const modalHandles = modalRoot ? instances.get(modalRoot) : null;
  if (!modalRoot || !modalHandles || !modalHandles.dialogHost) return;

  const { interestButtons, sourceField, triggerField, productField, dialogHost, syncInterestField } = modalHandles;
  let lastTrigger = null;

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest(TRIGGER_SELECTOR) : null;
    if (!trigger) return;

    event.preventDefault();
    lastTrigger = trigger;

    if (triggerField instanceof HTMLInputElement) {
      triggerField.value = (trigger.textContent || '').trim();
    }
    if (sourceField instanceof HTMLInputElement) {
      sourceField.value = window.location.href;
    }
    // Optional: a trigger can pass the product it's on via data-product="Name" —
    // cleared when absent so a later generic trigger doesn't carry over a stale value.
    if (productField instanceof HTMLInputElement) {
      productField.value = trigger.getAttribute('data-product') || '';
    }

    // Optional: a trigger can preselect a single interest card via data-interest="Value".
    const presetInterest = trigger.getAttribute('data-interest');
    if (presetInterest) {
      interestButtons.forEach((button) => {
        const isMatch = button.getAttribute('data-value') === presetInterest;
        button.setAttribute('aria-checked', isMatch ? 'true' : 'false');
      });
      syncInterestField();
    }

    if (typeof dialogHost.showDialog === 'function') {
      dialogHost.showDialog();
    }
  });

  dialogHost.addEventListener('dialog:close', () => {
    window.setTimeout(() => {
      if (lastTrigger instanceof HTMLElement) lastTrigger.focus();
    }, 300);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInquiryForms);
} else {
  initInquiryForms();
}
